/* eslint-disable stylistic/quote-props */
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'headerBlock',
  'featuredImage',
  'breadcrumbs',
  'infoRowContainer',
  'infoRow',
  'nameColumn',
  'name',
  'descriptionColumn',
  'description',
  'myShowroomIndicator',
  'myShowroomIndicatorIcon',
  'myShowroomIndicatorText',
  'anchorLinksContainer',
  'anchorLinksItem',
  'anchorLinksSwiperWrapper',
  'mapSection',
  'mapContainer',
  'mapOverlay',
  'mapOverlayAddress',
  'mapOverlayButton',
  'mapOverlayInfoSection',
  'mapOverlayInfoItem',
  'mapOverlayInfoText',
  'mapOverlayIcon',
  'mapOverlayParkingContainer',
  'mapOverlayDivider',
  'hoursColumn',
  'hoursHeading',
  'hoursTable',
  'supportBlock',
  'onDisplaySection',
  'onDisplayHeading',
  'onDisplayTitle',
  'onDisplayContent',
  'onDisplayGrid',
  'onDisplayItem',
  'sectionSeparator'
]);

/**
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 */
export const showroomDetailsStyles = helpers.defineMultiStyleConfig({
  baseStyle: () => ({
    root: {},

    headerBlock: {
      mb: 6,
    },

    featuredImage: {
      minHeight: {base: '200px', md: '300px', lg: '400px'},
      mb: 6,
      overflow: 'hidden',
      position: 'relative',
      w: '100%',
      sx: {
        img: {
          maxWidth: '100%',
          height: 'auto',
          width: '100%',
        },
      },
    },

    breadcrumbs: {
      marginTop: {base: '48px', lg: '46px'},
    },

    infoRowContainer: {
      marginTop: {base: '32px', lg: '56px'},
      marginBottom: {base: '38px', lg: '57px'},
      display: 'flex',
      flexDirection: 'column',
      sx: {
        'p': {
          fontSize: {base: '16px', lg: '20px'},
        },
        '.my-showroom-indicator-container .chakra-text': {
          fontSize: {base: '12px', lg: '14px'},
        },
      },
    },

    infoRow: {
      alignItems: {base: 'flex-start', lg: 'flex-end'},
      display: 'flex',
      flexDirection: {base: 'column', lg: 'row'},
      gap: {base: '12px', lg: '150px'},
    },

    nameColumn: {
      flex: {base: '1', lg: '1'},
    },

    nameStore: {
      color: 'brandColors.greyscale90',
      fontSize: {base: '40px', lg: '72px'},
      fontWeight: {base: '500', lg: '400'},
      lineHeight: '120%',
      letterSpacing: {base: '-0.4px;', lg: '-0.72px;'},
    },

    descriptionColumn: {
      display: 'flex',
      alignItems: 'flex-start',
      flex: {base: '1', lg: '1'},
      flexDirection: 'column',
      fontSize: {base: '16px', lg: '20px'},
      lineHeight: {base: '160%', lg: '150%'},
      sx: {
        '.my-showroom-indicator-text': {
          color: 'brandColors.greyscale90',
          fontSize: {base: '12px', lg: '14px'},
        },
      },
    },

    description: {
      color: 'gray.600',
      fontSize: {base: 'md', lg: 'lg'},
      lineHeight: 'tall',
    },

    myShowroomIndicator: {
      alignItems: 'center',
      backgroundColor: 'rgba(198, 189, 161, 0.15)',
      borderRadius: '4px',
      color: 'brandColors.greyscale90',
      display: 'flex',
      flexDirection: 'row',
      padding: '10px 12px',
      gap: '8px',
      marginTop: {base: '32px', lg: '20px'},
    },

    myShowroomIndicatorIcon: {
      boxSize: {base: '12px', lg: '14px'},
    },

    myShowroomIndicatorText: {
      color: 'brandColors.greyscale90',
      fontSize: {base: '12px', lg: '14px'},
      fontWeight: '500',
    },

    anchorLinksContainer: {
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      paddingBottom: {base: '48px', lg: '35px'},
      paddingTop: {base: '32px', lg: '23px'},
      sx: {
        '.anchor-links-container': {
          marginBottom: 0,
        },
        'a:not(:last-of-type)': {
          color: 'brandColors.greyscale90',
          fontSize: {base: '14px', lg: '18px'},
          fontWeight: '500',
          lineHeight: '120%',
        },
        '.chakra-link': {
          gap: '6px',
        },
        '.chakra-link .chakra-text': {
          color: 'brandColors.greyscale90',
          fontSize: {base: '14px', lg: '18px'},
          fontWeight: '500',
          lineHeight: '120%',
        },
        'img': {
          maxWidth: {base: '12px', lg: '16px'},
          maxHeight: {base: '12px', lg: '16px'},
        },
      },
    },

    anchorLinksItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: 'brandColors.greyscale90',
      fontSize: {base: '14px', lg: '18px'},
      fontWeight: '500',
      lineHeight: '120%',
      whiteSpace: 'nowrap',
    },

    mapSection: {
      gap: 0,
      templateColumns: {base: '1fr', lg: 'auto 440px'},
      marginX: {base: '-20px', lg: 0},
    },

    mapContainer: {
      h: {base: '640px', lg: '100%'},
      overflow: 'hidden',
      position: 'relative',
    },

    mapOverlay: {
      bg: 'rgba(255, 255, 255, 0.6)',
      borderRadius: '4px',
      backdropFilter: 'blur(10px)',
      left: {base: '50%', lg: '32px'},
      maxW: {base: '335px', lg: '383px'},
      p: {base: '28px', lg: '42px'},
      position: 'absolute',
      top: {base: '16px', lg: '32px'},
      transform: {base: 'translateX(-50%)', lg: 'none'},
      w: {base: 'calc(100% - 32px)', lg: 'auto'},
      zIndex: 10,
    },

    mapOverlayAddress: {
      color: 'brandColors.greyscale90',
      fontSize: {base: '18px', lg: '22px'},
      fontWeight: '500',
      lineHeight: '120%',
    },

    mapOverlayButton: {
      bg: 'black',
      color: 'white',
      fontSize: '14px',
      fontWeight: '600',
      minHeight: '54px',
      w: 'full',
      _hover: {
        bg: 'brandColors.greyscale80',
      },
    },

    mapOverlayInfoSection: {
      align: 'stretch',
      pt: 0,
      spacing: {base: '12px', lg: '16px'},
    },

    mapOverlayInfoItem: {
      spacing: {base: '12px', lg: '12px'},
    },

    mapOverlayInfoText: {
      fontSize: {base: '14px', lg: '16px'},
      lineHeight: '120%',
    },

    mapOverlayDivider: {
      backgroundColor: 'background.secondary',
      display: 'block',
      height: '1px',
      marginY: '8px',
      width: '100%',
    },

    mapOverlayIcon: {
      boxSize: {base: '24px', lg: '24px'},
      color: 'brandColors.greyscale90',
      flexShrink: 0,
    },

    mapOverlayParkingContainer: {
      align: 'stretch',
      spacing: '16px',
    },

    hoursColumn: {
      backgroundColor: 'brandColors.secondaryBG',
      padding: {base: '40px 20px', lg: '56px 50px 50px'},
    },

    hoursHeading: {
      color: 'black',
      fontSize: {base: '18px', lg: '22px'},
      fontWeight: '500',
      lineHeight: '120%',
      letterSpacing: {base: 'normal', lg: '-0.22px;'},
      mb: '16px',
    },

    hoursTable: {
      'table': {
        borderCollapse: 'collapse',
        color: 'brandColors.greyscale90',
        fontSize: {base: '14px', lg: '16px'},
        width: '100%',
      },
      'th, td': {
        padding: '0 8px 0 0',
        textAlign: 'left',
        verticalAlign: 'top',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      },
      'th': {
        fontWeight: '400',
        paddingRight: '16px',
      },
      'td': {
        padding: '0 8px 8px 0',
        verticalAlign: 'top',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      },
      'td:first-of-type': {
        paddingRight: '16px',
      },
      'td:nth-of-type(2)': {
        paddingRight: '16px',
      },
      'td:last-child': {
        textAlign: 'right',
        paddingRight: '0',
      },
    },

    supportBlock: {
      sx: {
        '.support-block-container': {
          paddingTop: {base: '0', lg: '20px'},
          paddingBottom: {base: '0', lg: '50px'},
        },
        '.component--container': {
          paddingX: '0',
          wordBreak: 'break-all',
        },
        '.component--container .chakra-link': {
          borderColor: 'black',
          bg: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '54px',
          paddingY: '14px',
          textDecoration: 'none',
          _hover: {
            bg: 'white',
            borderColor: 'black',
            color: 'white',
            textDecoration: 'none',
          },
        },
        '.component--container .chakra-link .chakra-text': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 'initial',
        },
      },
    },

    onDisplaySection: {
      display: {base: 'flex', lg: 'grid'},
      gridTemplateColumns: {lg: '290px minmax(0, 1fr) minmax(0, 1fr)'},
      direction: {base: 'column', lg: 'row'},
      gridColumnGap: {lg: '40px'},
      rowGap: {base: '24px'},
      marginTop: {base: '40px', lg: '60px'},
      marginBottom: {base: '40px', lg: '60px'},
    },

    onDisplayHeading: {
      flex: {base: '1', lg: '1'},
      minW: {lg: '33.33%'},
    },

    onDisplayTitle: {
      fontSize: {base: '28px', lg: '40px'},
      fontWeight: '500',
      lineHeight: '120%',
      letterSpacing: {base: '-0.28px', lg: '-0.4px;'},
    },

    onDisplayContent: {
      flex: {base: '1', lg: '2'},
      gridColumn: {lg: '2 / 4'},
    },

    onDisplayGrid: {
      display: 'grid',
      gridTemplateColumns: {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)'},
      gridColumnGap: {base: '12px', md: '12px', lg: '6px'},
      gridRowGap: {base: '16px', md: '16px', lg: '28px'},
    },

    onDisplayItem: {
      // Each icon item - adjust width based on how many you want per row
      flexGrow: 0,
      flexShrink: 0,
      sx: {
        '.component--icon': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: '16px',
        },
        '.chakra-text': {
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '120%',
          marginTop: 0,
        },
        'img': {
          maxWidth: '32px',
          maxHeight: '32px',
        },
      },
    },

    sectionSeparator: {
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      margin: {base: '40px auto', lg: '60px auto'},
      maxWidth: {base: '100%', lg: '1280px'},
      width: {base: 'calc(100% - 40px)', lg: '100%'},
    },

    sectionWrapper: {
      maxWidth: {base: '100%', lg: '1440px'},
      marginX: 'auto',
      paddingX: {base: '20px', lg: '80px'},
      width: '100%',
      // Offset for anchor link scrolling to account for sticky header
      scrollMarginTop: {base: '130px', lg: '150px'},
    },
    anchorLinksSwiperWrapper: {
      position: 'relative',
      sx: {
        '.swiper': {
          overflow: 'visible',
          width: '100%',
        },
        '.swiper-wrapper': {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        '.swiper-slide': {
          width: 'auto !important',
        },
      },
    },

    anchorLinksSwiper: {
      position: 'relative',
      '.swiper-wrapper': {
        overflow: 'visible',
      },
      sx: {
        // Base styles for both next and prev buttons
        '.swiper-button-next, .swiper-button-prev': {
          display: 'flex',
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '30px',
          height: '30px',
          backgroundColor: 'brandColors.greyscale20',
          borderRadius: '50%',
          color: 'white',
          margin: '0',
          zIndex: 10,
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: 'none',
          '&::after': {
            content: '""',
            display: 'block',
            width: '12px',
            height: '14px',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
          },
          '&:hover': {
            backgroundColor: 'brandColors.greyscale30',
          },
        },
        // Specific positioning for prev button
        '.swiper-button-prev': {
          left: '0',
          '&::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
        },
        // Specific positioning for next button
        '.swiper-button-next': {
          right: '0',
          '&::after': {
            backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
          },
        },
        // Hide only disabled buttons
        '.swiper-button-disabled': {
          display: 'none',
        },
      },
    },
  }),
});
